import { ChevronDown, Download, FileText, Loader2, Table2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
	downloadReportingCsv,
	type ReportingExport,
	type ReportingExportFormat,
	snapshotReportingExport,
} from "./reportingExport";

export function ReportingExportButton({
	data,
	disabled = false,
}: {
	data: ReportingExport;
	disabled?: boolean;
}) {
	const [exporting, setExporting] = useState(false);
	const inProgress = useRef(false);

	async function exportFile(format: ReportingExportFormat) {
		if (inProgress.current || disabled) return;
		if (data.entries.length === 0) {
			toast.error("No entries to export", {
				description: "Adjust the reporting filters and try again.",
			});
			return;
		}
		inProgress.current = true;
		setExporting(true);
		try {
			const snapshot = snapshotReportingExport(data);
			if (format === "csv") {
				downloadReportingCsv(snapshot);
			} else {
				const { downloadReportingPdf } = await import("./reportingPdf");
				await downloadReportingPdf(snapshot);
			}
			toast.success(`${format.toUpperCase()} exported`, {
				description: `${snapshot.entries.length} ${snapshot.entries.length === 1 ? "entry" : "entries"} downloaded`,
			});
		} catch {
			toast.error(`Failed to export ${format.toUpperCase()}`, {
				description: "Please try again.",
			});
		} finally {
			inProgress.current = false;
			setExporting(false);
		}
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="flex h-8 min-w-[120px] items-center justify-center gap-1.5 rounded-md border px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
					disabled={disabled || exporting}
					aria-label={
						exporting ? "Exporting time entries" : "Export time entries"
					}
					aria-busy={exporting}
				>
					{exporting ? (
						<Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
					) : (
						<Download
							aria-hidden="true"
							className="size-3.5 text-muted-foreground"
						/>
					)}
					{exporting ? "Exporting…" : "Export"}
					<ChevronDown aria-hidden="true" className="size-3" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuItem
					disabled={exporting || disabled}
					onSelect={() => void exportFile("csv")}
				>
					<Table2 aria-hidden="true" /> Export CSV
				</DropdownMenuItem>
				<DropdownMenuItem
					disabled={exporting || disabled}
					onSelect={() => void exportFile("pdf")}
				>
					<FileText aria-hidden="true" /> Export PDF
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
